import React, { useState } from 'react';
import useApi from '../../hooks/useApi';
import haxios from '../../lib/haxios';
import { PencilSquareIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';

export default function MaintenanceTab({ vehicleId }) {
    const { data: maintenanceResponse, refetch, isLoading } = useApi({ url: `/visiondashboard/salesassist/vehicles/${vehicleId}/maintenance`, initial: { data: [] } });

    const maintenanceRecords = maintenanceResponse?.data || [];
    const totalSpent = maintenanceRecords.reduce((sum, record) => sum + (parseFloat(record.cost) || 0), 0);

    const [formData, setFormData] = useState({ description: '', cost: '', date_completed: '' });
    const [showForm, setShowForm] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        if (!formData.description || !formData.cost || !formData.date_completed) {
            alert('All fields are required.');
            return;
        }

        setIsSaving(true);
        try {
            await haxios.post(`/visiondashboard/salesassist/vehicles/${vehicleId}/maintenance`, formData);
            refetch();
            setFormData({ description: '', cost: '', date_completed: '' });
            setShowForm(false);
        } catch (error) {
            console.error('Error saving maintenance record:', error);
            alert('Failed to save maintenance record.');
        }
        setIsSaving(false);
    };

    const handleDelete = async (recordId) => {
        if (!window.confirm('Are you sure you want to delete this record?')) return;
        try {
            await haxios.delete(`/visiondashboard/salesassist/vehicles/maintenance/${recordId}`);
            refetch();
        } catch (error) {
            console.error('Error deleting maintenance record:', error.response?.data || error);
            alert('Failed to delete maintenance record.');
        }
    };

    return (
        <div className="p-4">
            <h2 className="text-lg font-semibold mb-4">Vehicle Maintenance</h2>

            <div className="mb-4 flex justify-between items-center">
                <button
                    onClick={() => setShowForm(!showForm)}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-md flex items-center"
                >
                    <PlusIcon className="w-5 h-5 mr-2" />
                    {showForm ? 'Cancel' : 'Add Maintenance Record'}
                </button>

                <div className="text-lg font-semibold">Total Spent: £{Number(totalSpent).toFixed(2)}</div>
            </div>

            {showForm && (
                <div className="p-4 border rounded-lg bg-gray-50 mb-6">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Description</label>
                            <input
                                type="text"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                className="mt-1 block w-full border rounded-md p-2"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Cost (£)</label>
                            <input
                                type="number"
                                name="cost"
                                value={formData.cost}
                                onChange={handleInputChange}
                                className="mt-1 block w-full border rounded-md p-2"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Date Completed</label>
                            <input
                                type="date"
                                name="date_completed"
                                value={formData.date_completed}
                                onChange={handleInputChange}
                                className="mt-1 block w-full border rounded-md p-2"
                            />
                        </div>
                    </div>
                    <div className="mt-4">
                        <button
                            onClick={handleSave}
                            className="bg-green-600 text-white px-4 py-2 rounded-md"
                            disabled={isSaving}
                        >
                            {isSaving ? 'Saving...' : 'Save Record'}
                        </button>
                    </div>
                </div>
            )}

            {isLoading ? (
                <p className="text-center text-gray-600">Loading maintenance history...</p>
            ) : maintenanceRecords.length > 0 ? (
                <table className="w-full mt-4 bg-white border border-gray-200 shadow-md">
                    <thead className="bg-gray-200">
                        <tr>
                            <th className="py-2 px-4 text-left">Completed</th>
                            <th className="py-2 px-4 text-left">Description</th>
                            <th className="py-2 px-4 text-left">Cost (£)</th>
                            <th className="py-2 px-4 text-left">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {maintenanceRecords.map((record) => (
                            <tr key={record.id} className="border-t">
                                <td className="py-2 px-4">
                                    {record.date_completed
                                        ? new Date(record.date_completed).toLocaleDateString('en-GB')
                                        : 'Pending'}
                                </td>
                                <td className="py-2 px-4">{record.description}</td>
                                <td className="py-2 px-4">£{(parseFloat(record.cost) || 0).toFixed(2)}</td>
                                <td className="py-2 px-4 flex space-x-2">
                                    <button onClick={() => handleDelete(record.id)} className="p-1 rounded-full hover:bg-red-100">
                                        <TrashIcon className="w-5 h-5 text-red-600" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <p className="text-center text-gray-500">No maintenance records found.</p>
            )}
        </div>
    );
}
